"use client"

import clsx from "clsx"
import useEmblaCarousel from "embla-carousel-react"
import Image from "next/image"
import React, { useCallback, useEffect, useState } from "react"

import ArrowCarouselDark from "../../../../public/icons/arrow-carousel-dark.svg"
import ArrowCarousel from "../../../../public/icons/arrow-carousel.svg"
import Button, { ButtonProps } from "../Button"
import PageGrid from "../PageGrid"
import Quote from "../Quote"

/**
 * Functional component for the Quote Carousel
 *
 * @component
 * @param data - data pulled in from the graphql
 * @param staticData - data created from an object passed to this component
 */

interface quoteData {
  contentfulData?: any
  staticData?: Array<{
    quote?: string
    logo?: string | any | React.ReactNode
    name?: string
    quoteeImage?: {
      url: string
      title: string
    }
    companyImage?: {
      url: string
      title: string
    }
    title?: string
  }>
  buttonData?: React.ReactNode
  quoteMarkColor?: string
  textColorOverrideClass?: string
  linkTo?: string
  theme: ButtonProps["theme"]
  hasButton?: boolean
}

const QuoteCarousel: React.FC<quoteData> = ({
  contentfulData,
  staticData,
  buttonData,
  quoteMarkColor,
  textColorOverrideClass,
  linkTo = "/customer-case-study",
  theme = "dark",
  hasButton = true,
}) => {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    align: "center",
    skipSnaps: false,
    dragFree: false,
    duration: 20,
  })
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [canScrollPrev, setCanScrollPrev] = useState(false)
  const [canScrollNext, setCanScrollNext] = useState(false)

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  )

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const onSelect = useCallback((api: any) => {
    setSelectedIndex(api.selectedScrollSnap())
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  useEffect(() => {
    if (!emblaApi) return
    onSelect(emblaApi)
    emblaApi.on("select", onSelect)
  }, [emblaApi, onSelect])

  const Arrow = theme === "dark" ? ArrowCarousel : ArrowCarouselDark

  if (staticData) {
    return (
      <>
        {/* Embla viewport for static data */}
        <div className="embla" ref={emblaRef}>
          <div className="embla__container">
            {staticData?.map((data) => (
              <div key={data.quote} className="embla__slide">
                <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12 xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg">
                  <div className="hidden p-4 col-span-full lg:col-span-4 lg:!block">
                    <Image
                      src={data.companyImage.url}
                      alt={data.companyImage.title}
                      width={191}
                      height={38}
                      sizes="(min-width: 191px) 191px, 100vw"
                    />
                  </div>
                  <div className="p-4 col-span-full lg:col-span-8">
                    {/* Need the "!" in order to override the gatsby production build class */}
                    {data.logo && (
                      <div className="mb-6 block w-auto lg:!hidden">
                        <Image
                          src={data.companyImage.url}
                          alt={data.companyImage.title}
                          width={191}
                          height={38}
                          sizes="(min-width: 191px) 191px, 100vw"
                        />
                      </div>
                    )}
                    <Quote
                      {...data}
                      quote={data.quote}
                      name={data.name}
                      caseStudyLink={false}
                      key={data.quote}
                      quoteMarkColor={quoteMarkColor}
                      textColorOverrideClass={textColorOverrideClass}
                      quoteeDescription={data.title}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Show the arrows when the the UI is small and there are more than 3 dots */}
        <div
          className={clsx(
            "flex pl-4 mt-10 md:mt-4",
            staticData.length > 3 ? "xs:block md:hidden" : "xs:hidden",
          )}
        >
          <button
            onClick={scrollPrev}
            disabled={!canScrollPrev}
            className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50"
            type="button"
            aria-label="Previous slide"
          >
            <Arrow className="mr-10 transform -rotate-180 h-14 w-14" />
          </button>
          <button
            onClick={scrollNext}
            disabled={!canScrollNext}
            className="disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-50"
            type="button"
            aria-label="Next slide"
          >
            <Arrow className="h-14 w-14" />
          </button>
        </div>

        {/* Show the dots when the the UI is large enough and there are more than 3 dots */}
        <PageGrid
          className={clsx(
            "mx-0",
            staticData.length > 3 ? "xs:hidden md:grid" : "xs:grid",
          )}
        >
          <div className="p-4 col-span-full lg:col-start-5 lg:col-span-8">
            <div className="flex items-center mb-10 -ml-3">
              {staticData?.map(
                (data, index) =>
                  staticData.length > 1 && (
                    <button
                      key={data.quote}
                      onClick={() => scrollTo(index)}
                      className="p-4"
                      type="button"
                      aria-label={`Go to slide ${index + 1}`}
                    >
                      <div
                        className={clsx(
                          "rounded-full mr-8",
                          selectedIndex === index
                            ? "bg-blue-50 w-3 h-3"
                            : "w-2 h-2 bg-gray-60",
                        )}
                      />
                    </button>
                  ),
              )}
            </div>
            {buttonData ||
              (hasButton && (
                <Button to={linkTo} theme={theme} variant="tertiary">
                  Hear from more customers
                </Button>
              ))}
          </div>
        </PageGrid>
      </>
    )
  }

  return (
    <>
      {/* Embla viewport for contentful data */}
      <div className="embla" ref={emblaRef}>
        <div className="embla__container">
          {contentfulData?.map(({ title, colorLogo, quote }) => (
            <div key={title} className="embla__slide">
              <div className="grid grid-cols-2 gap-x-4 md:grid-cols-4 md:gap-x-8 lg:gap-x-10 xl:gap-x-12 lg:grid-cols-12 xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg">
                {/* Need the "!" in order to override the gatsby production build class */}
                <div className="hidden p-4 col-span-full lg:col-span-4 lg:!block">
                  <Image
                    src={colorLogo.url}
                    alt={title}
                    className="w-40 md:w-52"
                    width={256}
                    height={78}
                    sizes="(min-width: 256px) 256px, 100vw"
                  />
                </div>
                <div className="p-4 col-span-full lg:col-span-8">
                  {/* Need the "!" in order to override the gatsby production build class */}
                  <Image
                    src={colorLogo?.url}
                    alt={quote?.name || ""}
                    className="block w-40 mb-12 md:w-52 lg:!hidden"
                    width={256}
                    height={78}
                    sizes="(min-width: 256px) 256px, 100vw"
                  />
                  <Quote
                    quote={quote?.quote}
                    name={quote?.name}
                    quoteeDescription={quote?.title}
                    quoteeImage={quote?.quoteeImage}
                    caseStudyLink={false}
                    quoteMarkColor={quoteMarkColor}
                    textColorOverrideClass={textColorOverrideClass}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <PageGrid className="mx-0">
        <div className="col-span-full lg:col-span-4" />
        <div className="p-4 col-span-full lg:col-span-8">
          <div className="flex items-center mb-10 -ml-3">
            {contentfulData?.map(({ quote }, index) => (
              <button
                key={quote?.name}
                onClick={() => scrollTo(index)}
                className="p-4"
                type="button"
                aria-label={`Go to slide ${index + 1}`}
              >
                <div
                  className={clsx(
                    "rounded-full mr-8",
                    selectedIndex === index
                      ? "bg-blue-50 w-3 h-3"
                      : "w-1.5 h-1.5 bg-gray-60",
                  )}
                />
              </button>
            ))}
          </div>
          <Button to={linkTo} theme="light" variant="tertiary">
            Hear from more customers
          </Button>
        </div>
      </PageGrid>
    </>
  )
}

export default QuoteCarousel
